import React from "react";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";

const getInitialState = (workspaceId) => ({
  isOpen: (() => {
    try {
      const stored = localStorage.getItem(`${workspaceId}-ai-sidebar-open`);
      return stored === "true";
    } catch {
      return false;
    }
  })(),
  versionId: (() => {
    try {
      return (
        localStorage.getItem(`${workspaceId}-ai-sidebar-version-id`) || null
      );
    } catch {
      return null;
    }
  })(),
});

const sharedStates = new Map();

const listeners = new Map(); // Map<workspaceId, Set<listeners>>

const notifyListeners = (workspaceId, state) => {
  const workspaceListeners = listeners.get(workspaceId);
  if (workspaceListeners) {
    workspaceListeners.forEach((listener) => listener(state));
  }
};

const updateSharedState = (workspaceId, newState) => {
  const currentState =
    sharedStates.get(workspaceId) || getInitialState(workspaceId);
  const updatedState = { ...currentState, ...newState };
  sharedStates.set(workspaceId, updatedState);
  notifyListeners(workspaceId, updatedState);
};

export const useAISidebar = () => {
  const workspace = useWorkspace();
  const { id: workspaceId } = workspace;
  const currentState =
    sharedStates.get(workspaceId) || getInitialState(workspaceId);
  const [state, setState] = React.useState(currentState);

  React.useEffect(() => {
    // Initialize shared state for this workspace if not exists
    if (!sharedStates.has(workspaceId)) {
      sharedStates.set(workspaceId, getInitialState(workspaceId));
    }

    // Set up listeners for this workspace
    if (!listeners.has(workspaceId)) {
      listeners.set(workspaceId, new Set());
    }

    const listener = (newState) => setState(newState);
    listeners.get(workspaceId).add(listener);

    return () => {
      const workspaceListeners = listeners.get(workspaceId);
      if (workspaceListeners) {
        workspaceListeners.delete(listener);
        if (workspaceListeners.size === 0) {
          listeners.delete(workspaceId);
        }
      }
    };
  }, [workspaceId]);

  const updateSidebarState = React.useCallback(
    (open, version = null) => {
      try {
        localStorage.setItem(`${workspaceId}-ai-sidebar-open`, open.toString());
        if (version) {
          localStorage.setItem(`${workspaceId}-ai-sidebar-version-id`, version);
        } else if (version === null) {
          localStorage.removeItem(`${workspaceId}-ai-sidebar-version-id`);
        }
      } catch {
        // Ignore localStorage errors
      }
      updateSharedState(workspaceId, { isOpen: open, versionId: version });
    },
    [workspaceId]
  );

  const toggleAI = React.useCallback(() => {
    updateSidebarState(!state.isOpen, state.versionId);
  }, [state.isOpen, state.versionId, updateSidebarState]);

  const closeAI = React.useCallback(() => {
    updateSidebarState(false, state.versionId);
  }, [state.versionId, updateSidebarState]);

  const clearAI = React.useCallback(() => {
    try {
      localStorage.removeItem(`${workspaceId}-ai-sidebar-open`);
      localStorage.removeItem(`${workspaceId}-ai-sidebar-version-id`);
    } catch {
      // Ignore localStorage errors
    }
    updateSharedState(workspaceId, { isOpen: false, versionId: null });
  }, [workspaceId]);

  return {
    isOpen: state.isOpen,
    versionId: state.versionId,
    toggleAI,
    closeAI,
    clearAI,
    updateSidebarState,
  };
};
